<template>
    <div class="employee-progress-table">
        <el-table :data="items" :row-key="getRowKey" stripe class="domain-table"
            :show-overflow-tooltip="false" table-layout="fixed"
            :empty-text="loading ? 'Đang tải...' : 'Không có dữ liệu'" max-height="600">
            <el-table-column prop="rank" label="Top Team" width="100">
                <template slot-scope="{row}">
                    <span>{{row.top_team?.rank}}</span>
                </template>
            </el-table-column>
            <el-table-column prop="periodLabel" label="Thời gian" width="120">
                <template slot-scope="{row}">
                    <span>Quý {{convertToRoman(row.quarter)}}/{{row.year}}</span>
                </template>
            </el-table-column>
            <el-table-column prop="position" label="Team" width="100">
                <template slot-scope="{row}">
                    <span>{{row.department?.name}}</span>
                </template>
            </el-table-column>
            <el-table-column prop="exp" label="Điểm EXP" width="100">
                <template slot-scope="{row}">
                    <span>{{row.exp_points ?? 0}}</span>
                </template>
            </el-table-column>
            <el-table-column prop="total" label="Tổng điểm" width="100">
                <template slot-scope="{ row }">
                    <el-tooltip v-if="row.top_company?.total_score" content="Giải thích: Tổng điểm / điểm cần đạt để tăng level" placement="top" effect="light">
                        <span style="color: #4D1EAB; font-weight: 600;">{{ parseInt(row.top_company.total_score) }}/{{row.requested_level.score}}</span>
                    </el-tooltip>
                    <span v-else class="text-danger">0/ {{ row.requested_level.score }}</span>
                </template>
            </el-table-column>
            <el-table-column prop="companyRank" label="Top Công ty" width="100">
                <template slot-scope="{row}">
                    <span>{{row?.top_company?.rank}}</span>
                </template>
            </el-table-column>

            <el-table-column :min-width="600">
                <template slot="header">
                    <div class="level-scale">
                        <span>Trainee</span>
                        <span>Junior 1</span>
                        <span>Junior 2</span>
                        <span>Member 1</span>
                        <span>Member 2</span>
                        <span>Senior 1</span>
                        <span>Senior 2</span>
                    </div>
                </template>
                <template slot-scope="{row}">
                    <div class="track">
                        <el-tooltip effect="light" placement="right">
                            <div v-if="getPointNeeded(row)" slot="content">
                                Bạn cần đạt <b>{{ getPointNeeded(row) }}</b> điểm để đạt level tiếp theo
                            </div>
                            <div v-else slot="content">
                                Bạn đã đủ điểm để đạt level tiếp theo 😍
                            </div>
                            <div class="bar admin" :style="barWidth(overallWidth(row.current_level.level))"
                            :data-label="currentLabel(row)">
                            </div>
                        </el-tooltip>
                    </div>
                </template>
            </el-table-column>
            <template slot="empty">
                <div v-if="loading" class="loading-body">
                    <i class="el-icon-loading"></i>
                    <span>Đang tải...</span>
                </div>
            </template>
        </el-table>
    </div>
</template>

<script>
export default {
    name: 'EmployeeProgressTable',
    props: {
        items: { type: Array, default: () => [] },
        loading: { type: Boolean, default: false },
    },
    methods: {
        convertToRoman(num) {
            const romanNumerals = {
                1: 'I',
                2: 'II', 
                3: 'III',
                4: 'IV'
            };
            return romanNumerals[num] || num;
        },
        getRowKey(row) {
            return row.id || row.employeeId || `${row.fullName}-${row.periodLabel}`;
        },
        barWidth(percent) {
            const p = Math.max(0, Math.min(100, Number(percent || 0)));
            return { width: p + '%' };
        },
        overallWidth(bands) {
            const order = ['trainee', 'junior1', 'junior2', 'member1', 'member2', 'senior1', 'senior2'];

            if (typeof bands === 'string') {
                const normalized = bands.toLowerCase().trim();
                const level = normalized.match(/(senior|junior|member|trainee)/)?.[1];
                const number = normalized.match(/[12]/)?.[0];

                const normalizedBands = level === 'trainee' ? 'trainee' : `${level}${number || '2'}`;
                const idx = Math.max(0, order.indexOf(normalizedBands));
                // Thay đổi từ (idx + 1) thành (idx + 0.5) để bar kết thúc ở giữa mỗi mốc
                return ((idx + 0.5) / order.length) * 100;
            }

            const totalPercent = order.reduce((sum, key) => {
                const val = Math.max(0, Math.min(100, Number(bands?.[key] ?? 0)));
                return sum + val;
            }, 0);

            return Math.max(0, Math.min(100, (totalPercent / 700) * 100));
        },
        getPointNeeded(item) {
            const kra_score = item.kra_score ?? 0;
            const meeting_points = item.meeting_points ?? 0;
            const exp_points = item.exp_points ?? 0;
            const sum = parseInt(kra_score)+parseInt(meeting_points)+parseInt(exp_points)
            const denominator = parseInt(item.requested_level.score) ?? 0;
            if(denominator - sum > 0) {
                return denominator - sum;
            }
            return 0;
        },
        currentLabel(row) {
            return row.current_level.level;
        },
    }
};
</script>

<style lang="scss" scoped>
.track {
    position: relative;
    height: 26px;
}

.track .bar {
    height: 24px;
    border-radius: 20px;
    position: absolute;
    left: 0;
    transition: width .3s ease;
    /* keep only the fill animation while preserving original visuals */
    transform-origin: left center;
    transform: scaleX(0);
    animation: fill-grow 1.8s ease-in-out forwards;
}

/* labels always inside the bar */
.track .bar::after {
  content: attr(data-label);
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);     
  color: white;
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

 .track .bar.admin {
     background: #6D41C6;
 }

@keyframes fill-grow {
  0% { transform: scaleX(0); }
  100% { transform: scaleX(1); }
}

::v-deep {
    .domain-table {
        th,
        td,
        th .cell,
        td .cell {
            white-space: normal !important;
            word-break: keep-all !important;
            overflow: visible !important;
            max-width: none !important;
            height: auto !important;
            text-align: center !important;
        }

        th {
            background: #4d1eab !important;
            color: #fff !important;
            border-color: #4d1eab !important;
            line-height: 1.4 !important;
            padding: 16px 8px !important;
            height: 80px !important;
        }

        td {
            line-height: 1.3 !important;
            padding: 8px 4px !important;
            height: auto !important;
        }

        tr {
            height: 60px !important;
        }
    }
}

.level-scale {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    align-items: end;
    gap: 0;
}

.level-scale span {
    text-align: center;
    font-weight: 600;
}

.loading-body {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px;
    color: #666;
    
    i {
        font-size: 24px;
        margin-bottom: 12px;
        animation: rotating 2s linear infinite;
    }
    
    span {
        font-size: 14px;
    }
}

@keyframes rotating {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}
</style>